import type { RouteObject } from 'react-router-dom';
import { Outlet } from 'react-router-dom';

import AclosetShopInfoModal from '@/app/AclosetShopInfoModal';
import UpdateVersionModal from '@/app/UpdateVersionModal';
import { AI_상품정보_자동입력_동의_모달 } from '@/app/settings/sale-platform-extension/AI_상품정보_자동입력_동의_모달';
import ShopInfoProvider from '@/app/shop/shop-info';
import { isDeploy } from '@/environment';
import { C원스톱_솔루션_비용_입금_안내_모달 } from '@/features/one-stop-solution/정산내역/components/원스톱_솔루션_비용_입금_안내_모달';
import { 팝업_공지 } from '@/features/공지사항/팝업공지/팝업_공지';
import { C플랜_리뉴얼_안내_모달 } from '@/features/플랜/components/플랜_리뉴얼_안내_모달';

import 쇼핑몰_연동_설정_라우터 from './linkage/setting';
import { 카페24_상품_분류_연결_루트 } from './linkage/setting/cafe24';
import { MaintenanceGuard } from './maintenance/MaintenancePage';
import managementRouters from './management';
import { MobileRedirect } from './mobileRedirect/MobileRedirect';
import myInfoRouters from './myInfo';
import { 원스톱_솔루션_라우터_루트 } from './one-stop-solution/routes';
import { 결제_페이지_루트 } from './payment';
import { 플랜_라우터_루트 } from './plan/routes';
import { 인스타그램_상품글_편집기_루트 } from './products/instagram-post-editor/routes';
import 설정_메뉴_라우터 from './settings';
import { 필수_설정_도우미_루트 } from './settings/assistant';
import { 원스톱_솔루션_설정_라우터_루트 } from './settings/one-stop-solution/routes';
import { 상세설명_편집기_루트 } from './settings/platform-post/상세설명_편집기/routes';
import 원클릭_판매확장_설정_라우터 from './settings/sale-platform-extension';
import BlockedShopGuard from './shop/BlockedShopGuard';
import { 상품글_미리보기_루트 } from './task/상품글_미리보기/routes';
import { 이미지_전체_보기_루트 } from './task/이미지_전체_보기/routes';
import operationRouters from './upload';
import { 업로드_온보딩_루트 } from './업로드_온보딩';

/**
 * 샵 권한 & PC 레이아웃 & 샵 메인 레이아웃을 가지는 페이지들의 라우트들.
 *
 * ## 계층
 * - 샵 권한
 * - PC 레이아웃
 * - 샵 메인 레이아웃
 */
const mainLayoutRoute: RouteObject = {
  async lazy() {
    const { default: Layout } = await import('@/layout/mainLayout');
    return {
      Component: Layout,
    };
  },
  children: [
    !isDeploy
      ? {
          path: '/dev',
          async lazy() {
            const { default: Page } = await import('@/app/developer/DeveloperPage');
            return {
              Component: Page,
            };
          },
        }
      : {},
    ...operationRouters,
    ...managementRouters,
    ...myInfoRouters,
    ...설정_메뉴_라우터,
    ...쇼핑몰_연동_설정_라우터,
    ...원클릭_판매확장_설정_라우터,
    ...플랜_라우터_루트,
    ...원스톱_솔루션_라우터_루트,
    ...원스톱_솔루션_설정_라우터_루트,
  ],
};

/**
 * 샵 권한 & PC 레이아웃 라우트들.
 *
 * 각 children 에서 레이아웃을 정의하시면 됩니다.
 */
const shopRoute: RouteObject = {
  element: (
    <MobileRedirect>
      <Outlet />
      <팝업_공지 />
      <AclosetShopInfoModal />
      <UpdateVersionModal />
      <AI_상품정보_자동입력_동의_모달 />
      <C플랜_리뉴얼_안내_모달 />
      <C원스톱_솔루션_비용_입금_안내_모달 />
    </MobileRedirect>
  ),
  children: [
    mainLayoutRoute,
    인스타그램_상품글_편집기_루트,
    필수_설정_도우미_루트,
    상세설명_편집기_루트,
    이미지_전체_보기_루트,
    상품글_미리보기_루트,
    결제_페이지_루트,
    업로드_온보딩_루트,
    카페24_상품_분류_연결_루트,
  ],
};

/**
 * 샵 권한까지는 필요한 라우트들.
 */
export const shopAuthRoute: RouteObject = {
  element: (
    <ShopInfoProvider>
      <MaintenanceGuard>
        <BlockedShopGuard>
          <Outlet />
        </BlockedShopGuard>
      </MaintenanceGuard>
    </ShopInfoProvider>
  ),
  children: [
    shopRoute,
    {
      path: '/login/info/edit',
      async lazy() {
        const { default: Page } = await import('@/app/signUp/로그인_정보_설정_페이지');
        return {
          Component: Page,
        };
      },
    },
    {
      path: '/setting',
      async lazy() {
        const { default: Page } = await import('./회원정보/회원정보입력_페이지');
        return {
          Component: Page,
        };
      },
    },
    {
      path: '/welcome',
      async lazy() {
        const { default: Page } = await import('@/app/signUp/WelcomePage');
        return {
          Component: Page,
        };
      },
    },
  ],
};
